import {
  AuditBadge,
  Button,
  Card,
  CardContent,
  ConfirmSheet,
  ConnectionStatus,
  DangerActionPanel,
  MetricCard,
  PageHeader,
  StatusChip,
  TextareaField,
} from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { CalendarClock, ShieldAlert } from "lucide-react";
import { useState } from "react";
import { hasPermission } from "../../features/ops/permissions.ts";
import { useSessionTransition, useSocialPause, useStaffState } from "../../features/ops/queries.ts";
import type { VenueSessionStatus } from "../../features/ops/types.ts";
import { getStaffPrincipal } from "../../lib/auth-session.ts";

export const Route = createFileRoute("/_app/session-control")({
  component: SessionControlScreen,
});

const nextSessionAction: Record<VenueSessionStatus, string> = {
  scheduled: "Open session",
  open: "Move to last call",
  last_call: "Start closing",
  closing: "Close session",
  closed: "Closed",
};

const sessionTone: Record<VenueSessionStatus, "neutral" | "success" | "warning" | "danger"> = {
  scheduled: "neutral",
  open: "success",
  last_call: "warning",
  closing: "warning",
  closed: "neutral",
};

function SessionControlScreen() {
  const principal = getStaffPrincipal();
  const { data } = useStaffState();
  const transition = useSessionTransition();
  const socialPause = useSocialPause();
  const [transitionConfirmOpen, setTransitionConfirmOpen] = useState(false);
  const [socialConfirmOpen, setSocialConfirmOpen] = useState(false);
  const [socialReason, setSocialReason] = useState("");

  if (!data) return <PageHeader title="Session control" subtitle="Loading shift state…" />;

  const canManageSession = hasPermission(principal, "session:manage");
  const canPauseSocial = hasPermission(principal, "social:disable");
  const currentStatus = data.session.status;
  const nextLabel = nextSessionAction[currentStatus];
  const terminal = currentStatus === "closed";
  const socialActiveTables = data.tables.filter((table) => table.socialActive).length;
  const pendingOrders = data.orders.filter((order) =>
    ["pending", "accepted", "preparing", "ready"].includes(order.status),
  ).length;
  const acceptedGifts = data.gifts.filter((gift) => gift.status === "accepted").length;
  const pendingSocialItems = data.gifts.filter((gift) => gift.status === "pending").length;
  const openReports = data.reports.filter((report) =>
    ["submitted", "triaged", "escalated"].includes(report.status),
  ).length;
  const servedOrders = data.orders.filter((order) => order.status === "served").length;
  const fulfilledGifts = data.gifts.filter((gift) => gift.status === "fulfilled").length;
  const refundLikePayments = data.payments.filter((payment) =>
    ["refund_pending", "refunded", "voided"].includes(payment.status),
  ).length;
  const controlsDisabled = data.connection.offline || terminal || transition.isPending;
  // FR-STAFF-093 / SAFE-012: engaging the kill switch requires a reason;
  // re-enabling does not.
  const engagingSocialPause = !data.session.socialPaused;
  const socialReasonMissing = engagingSocialPause && socialReason.trim().length === 0;
  const socialDisabled = data.connection.offline || socialPause.isPending || socialReasonMissing;
  const sessionControlCaption = !canManageSession
    ? "Requires session:manage"
    : data.connection.offline
      ? "Blocked while offline"
      : terminal
        ? "No next transition"
        : "Audited state transition";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Session control / คุมรอบ"
        subtitle={`${data.branch.name} · ${data.session.label}`}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <ConnectionStatus
              degraded={data.connection.degraded}
              offline={data.connection.offline}
            />
            <StatusChip tone={sessionTone[currentStatus]}>{currentStatus}</StatusChip>
            <AuditBadge label="ops-only" />
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <MetricCard
          label="Session state"
          value={currentStatus}
          caption="scheduled → open → last_call → closing → closed"
          tone={sessionTone[currentStatus]}
        />
        <MetricCard
          label="Active tables"
          value={data.tables.filter((t) => t.status === "occupied").length}
        />
        <MetricCard
          label="Social-active tables"
          value={socialActiveTables}
          caption="operational indicator only"
          tone={socialActiveTables > 0 ? "info" : "neutral"}
        />
        <MetricCard
          label="Pending orders"
          value={pendingOrders}
          tone={pendingOrders > 0 ? "warning" : "neutral"}
        />
        <MetricCard
          label="Accepted gifts"
          value={acceptedGifts}
          tone={acceptedGifts > 0 ? "info" : "neutral"}
        />
        <MetricCard
          label="Open reports"
          value={openReports}
          tone={openReports > 0 ? "danger" : "neutral"}
        />
      </div>

      <Card>
        <CardContent className="space-y-5 p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="flex size-11 items-center justify-center rounded-xl bg-secondary/15 text-secondary">
                  <CalendarClock className="size-5" />
                </span>
                <div>
                  <h2 className="font-display font-semibold text-lg">VenueSession lifecycle</h2>
                  <p className="text-muted-foreground text-sm">
                    Move tonight through the canonical session states. Last call and closing visibly
                    gate customer social/ordering flows in mock state.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {["scheduled", "open", "last_call", "closing", "closed"].map((state) => (
                  <StatusChip
                    key={state}
                    tone={state === currentStatus ? sessionTone[currentStatus] : "neutral"}
                  >
                    {state}
                  </StatusChip>
                ))}
              </div>
            </div>
            <div className="space-y-2 sm:min-w-56">
              <Button
                className="min-h-12 w-full"
                disabled={!canManageSession || controlsDisabled}
                onClick={() => setTransitionConfirmOpen(true)}
              >
                {nextLabel}
              </Button>
              <p className="text-muted-foreground text-xs">{sessionControlCaption}</p>
            </div>
          </div>
          {!canManageSession ? (
            <p className="rounded-md border border-border bg-muted/40 p-3 text-muted-foreground text-sm">
              Session controls are hidden from non-manager navigation and remain read-only here if
              deep-linked.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {canPauseSocial ? (
        <div className="space-y-3">
          {engagingSocialPause ? (
            <Card className="border-destructive/30 bg-destructive/5">
              <CardContent className="p-5">
                <TextareaField
                  id="socialPauseReason"
                  label="Reason (required) / เหตุผล (จำเป็น)"
                  value={socialReason}
                  onChange={setSocialReason}
                  placeholder="e.g. Fight reported on the floor, zone A"
                  hint="Captured in the audit log and the auto-created Incident (FR-STAFF-093, SAFE-012)."
                />
              </CardContent>
            </Card>
          ) : null}
          <DangerActionPanel
            title={
              data.session.socialPaused
                ? "Emergency social mode is paused"
                : "Emergency social disable"
            }
            description="Suppresses Around, gifting, contact requests, and games for this VenueSession while leaving ordering available. Pending social gifts are voided in mock state and the action is audit logged."
            actionLabel={data.session.socialPaused ? "Re-enable social" : "Pause social now"}
            disabled={socialDisabled}
            onAction={() => setSocialConfirmOpen(true)}
          />
        </div>
      ) : (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="flex items-start gap-3 p-5">
            <ShieldAlert className="mt-1 size-5 shrink-0 text-destructive" />
            <div>
              <h2 className="font-display font-semibold text-destructive text-lg">
                Emergency social disable is restricted
              </h2>
              <p className="text-muted-foreground text-sm">
                Direct deep links stay read-only unless the staff session has social:disable.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display font-semibold text-lg">Operational session summary</h2>
            <p className="text-muted-foreground text-sm">
              {terminal ? "Closed-session wrap-up" : "Live/provisional wrap-up"} for handover; no
              customer-style discovery or contact details are exposed.
            </p>
          </div>
          <StatusChip tone={terminal ? "success" : "info"}>
            {terminal ? "final" : "provisional"}
          </StatusChip>
        </div>
        <div className="grid gap-3 md:grid-cols-4">
          <MetricCard
            label="Served orders"
            value={servedOrders}
            caption="closed operational loop"
          />
          <MetricCard
            label="Fulfilled gifts"
            value={fulfilledGifts}
            caption="accepted before fulfil"
          />
          <MetricCard
            label="Pending social items"
            value={pendingSocialItems}
            caption="voided when paused"
          />
          <MetricCard
            label="Refund/void queue"
            value={refundLikePayments}
            caption="cashier follow-up"
          />
        </div>
      </section>

      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <h2 className="font-display font-semibold text-lg">Recent session audit</h2>
        <div className="grid gap-2">
          {data.auditLog.slice(0, 5).map((entry) => (
            <div
              key={entry.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/70 p-3 text-sm"
            >
              <span className="font-medium">{entry.action}</span>
              <span className="text-muted-foreground">
                {entry.entityType} · {entry.after ?? "recorded"}
              </span>
            </div>
          ))}
        </div>
      </section>

      <ConfirmSheet
        open={transitionConfirmOpen}
        onOpenChange={setTransitionConfirmOpen}
        title={`${nextLabel}?`}
        description="This mock transition mirrors the server-owned VenueSession state machine and records an audit entry."
        confirmLabel={nextLabel}
        destructive={currentStatus === "closing"}
        onConfirm={() => transition.mutate()}
      />
      <ConfirmSheet
        open={socialConfirmOpen}
        onOpenChange={setSocialConfirmOpen}
        title={
          data.session.socialPaused ? "Re-enable social features?" : "Pause social features now?"
        }
        description={
          engagingSocialPause
            ? `Ordering remains available. Customer discovery, gifts, contact requests, and games are suppressed until re-enabled. Reason: "${socialReason.trim()}" (audit logged, opens an Incident).`
            : "Ordering remains available. Customer discovery, gifts, contact requests, and games are suppressed until re-enabled."
        }
        confirmLabel={data.session.socialPaused ? "Re-enable social" : "Pause social now"}
        destructive={!data.session.socialPaused}
        onConfirm={() => {
          socialPause.mutate({
            paused: !data.session.socialPaused,
            reason: engagingSocialPause ? socialReason.trim() : undefined,
          });
          if (engagingSocialPause) setSocialReason("");
        }}
      />
    </div>
  );
}
